import type { IFormGenContext, IFormGenSchema, TJSONSchema } from "@artifact-management/json-dynamic-form";
import ReplayIcon from "@mui/icons-material/Replay";
import { Alert, Grid, IconButton, Typography } from "@mui/material";
import type { FromSchema } from "json-schema-to-ts";
import type { BaseSyntheticEvent } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { FormGenElement } from "./Element";
import { type TResetForm, getFormGenRows } from "./logics";

export interface IFormGenControlledProps<Schema extends TJSONSchema, Values = FromSchema<Schema>> {
  schema: IFormGenSchema<Schema>;
  parsedJsonSchema: TJSONSchema;
  formControls: UseFormReturn<FieldValues>;
  context?: IFormGenContext;
  onSubmit?: (data: Values) => void;
  renderActions?: (submit: (event?: BaseSyntheticEvent) => void, reset: TResetForm<Values>) => React.ReactNode;
}

export const FormGenControlled = <Schema extends TJSONSchema, Values = FromSchema<Schema>>({
  schema,
  parsedJsonSchema,
  formControls,
  context,
  onSubmit,
  renderActions,
}: IFormGenControlledProps<Schema, Values>) => {
  const { control, setValue, handleSubmit, reset, subscribe } = formControls;

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <Alert
          severity="error"
          action={
            <IconButton onClick={resetErrorBoundary}>
              <ReplayIcon color="error" />
            </IconButton>
          }
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Oops !
          </Typography>
          <Typography variant="body1">Une erreur inattendue est survenue</Typography>
          {typeof error === "object" && error !== null && (
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {"toString" in error ? error.toString() : "Unknown error"}
            </Typography>
          )}
        </Alert>
      )}
    >
      <Grid container spacing={2}>
        {getFormGenRows(schema.uiSchema).map(({ startIndex, items }) => (
          <Grid key={startIndex} container size={12} spacing={2}>
            {items.map((item, offset) => (
              <FormGenElement
                // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is acceptable here because the list is static and does not change order
                key={startIndex + offset}
                control={control}
                setValue={setValue}
                subscribe={subscribe}
                schema={schema}
                parsedJsonSchema={parsedJsonSchema}
                uiSchemaIndex={startIndex + offset}
                uiSchemaElement={item}
                context={context}
              />
            ))}
          </Grid>
        ))}
        {renderActions?.(handleSubmit(onSubmit as (data: FieldValues) => void), reset as TResetForm<Values>)}
      </Grid>
    </ErrorBoundary>
  );
};
